import { useCallback, useEffect, useState } from 'react';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { GraphQLError } from 'graphql';

// Generic type for a graphql client
export declare class GraphQLClientError extends Error {
  message: string;
  graphQLErrors: ReadonlyArray<GraphQLError>;
}
export type GraphqlClientResponse<TData> = {
  data: TData | undefined;
  error: GraphQLClientError | Error | undefined;
};

export type GraphQLClient = {
  /**
   * Executes `document` — a query OR a mutation — and resolves the `{ data, error }` envelope;
   * it must never reject. Adapter authors: the returned `error` must carry a stable message and
   * no request/response internals — graphql-request's raw `ClientError.message` embeds the request
   * variables (e.g. a plaintext reset password). See `src/graphql/AGENTS.md`.
   */
  query<TData, TVars extends Record<string, unknown>>(
    document: TypedDocumentNode<TData, TVars>,
    vars: TVars,
  ): Promise<GraphqlClientResponse<TData>>;
};

export const useAsyncQuery = <TData>(
  queryFn: () => Promise<GraphqlClientResponse<TData>>,
): GraphqlClientResponse<TData> & { loading: boolean; refresh: () => void } => {
  const [runQuery, setRunQuery] = useState(true);
  const [fetchState, setFetchState] = useState<GraphqlClientResponse<TData> & { loading: boolean }>(
    {
      loading: true,
      error: undefined,
      data: undefined,
    },
  );
  const refreshFn = useCallback(() => setRunQuery(true), []);
  useEffect(() => {
    if (!runQuery) {
      return undefined;
    }

    let unmounted = false;
    async function execute() {
      try {
        const result = await queryFn();
        if (!unmounted) {
          setFetchState({ loading: false, data: result.data, error: result.error });
        }
      } catch (e) {
        if (!unmounted) {
          setFetchState({
            loading: false,
            data: undefined,
            error: e instanceof Error ? e : new Error('GraphQL request failed'),
          });
        }
      } finally {
        if (!unmounted) {
          setRunQuery(false);
        }
      }
    }
    void execute();

    // Check unmounted before changing state ensures that
    // 1) If the entire component unmounted, we don't change state.
    // 2) If runQuery or queryFn change, we don't mix up state between different queries.
    return () => {
      unmounted = true;
    };
  }, [runQuery, queryFn]);
  return {
    ...fetchState,
    refresh: refreshFn,
  };
};

export const useGraphqlQuery = <TData, TVars extends Record<string, unknown>>(
  client: GraphQLClient,
  document: TypedDocumentNode<TData, TVars>,
  vars: TVars,
): GraphqlClientResponse<TData> & { loading: boolean; refresh: () => void } => {
  const queryFn = useCallback(
    () => client.query(document, vars),
    // document is compared by identity (generated documents are module
    // constants); vars by value, hence the exhaustive-deps false positive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, document, JSON.stringify(vars)],
  );
  return useAsyncQuery(queryFn);
};
